import React from 'react'
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import { mainGreen } from '../../../utils/color'
import {
	accountTypeForOrder,
	accountTypeLabel,
	formatMoney,
	orderCode,
	orderContactText,
	orderItemsText,
	paymentLabel,
	sectionSx,
} from '../cashflowUtils'
import { EmptyRow } from './CashflowCommon'

const statusLabel = (status) => {
	if (status === 'confirmed') return 'Đã xác nhận'
	if (status === 'rejected') return 'Đã từ chối'
	return 'Chờ xác nhận'
}

const statusColor = (status) => {
	if (status === 'confirmed') return 'success'
	if (status === 'rejected') return 'error'
	return 'warning'
}

const orderTotal = (order) => (Number(order?.totalPrice) || 0) + (Number(order?.shippingTotal) || 0)

function InfoItem({ label, value, strong = false }) {
	return (
		<Box sx={{ minWidth: 0 }}>
			<Typography sx={{ color: '#637381', fontSize: 12 }}>{label}</Typography>
			<Typography sx={{ fontWeight: strong ? 900 : 700, overflowWrap: 'anywhere' }}>{value || '-'}</Typography>
		</Box>
	)
}

function ConfirmationTable({
	canManagePayment,
	confirmPaymentRequest,
	deletePaymentRequest,
	items,
	mode,
	rejectPaymentRequest,
}) {
	return (
		<Box className='cashflow-table-shell cashflow-scroll'>
			<Table size='small' sx={{ minWidth: 1040 }}>
				<TableHead>
					<TableRow>
						<TableCell>Đơn</TableCell>
						<TableCell>{mode === 'incoming' ? 'Cửa hàng xử lý' : 'Cửa hàng nhận tiền'}</TableCell>
						<TableCell>Tài khoản</TableCell>
						<TableCell>Ghi chú gửi kèm</TableCell>
						<TableCell>Trạng thái</TableCell>
						<TableCell align='right'>Số tiền</TableCell>
						<TableCell align='right'></TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{items.length === 0 && <EmptyRow colSpan={7} text='Chưa có yêu cầu' />}
					{items.map((item) => {
						const orderAccountType = accountTypeForOrder(item.order)
						const accountDisplay =
							orderAccountType === 'bank'
								? item.bankAccount?.name || 'Tài khoản đã ngưng'
								: `${accountTypeLabel(orderAccountType)} mặc định`
						return (
							<TableRow key={item._id}>
								<TableCell>
									<Typography sx={{ fontWeight: 900 }}>{orderCode(item.order)}</Typography>
								</TableCell>
								<TableCell>{mode === 'incoming' ? item.toStore?.name : item.fromStore?.name}</TableCell>
								<TableCell>{accountDisplay}</TableCell>
								<TableCell sx={{ minWidth: 220 }}>
									<Typography
										sx={{
											color: item.content ? '#263238' : '#919EAB',
											fontSize: 13,
											fontWeight: item.content ? 700 : 500,
											whiteSpace: 'pre-line',
											overflowWrap: 'anywhere',
										}}>
										{item.content || 'Không có ghi chú'}
									</Typography>
								</TableCell>
								<TableCell>
									<Chip size='small' color={statusColor(item.status)} label={statusLabel(item.status)} />
								</TableCell>
								<TableCell align='right' sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
									{formatMoney(item.amount)}
								</TableCell>
								<TableCell align='right'>
									{mode === 'incoming' && item.status === 'pending' && canManagePayment && (
										<Stack direction='row' spacing={0.5} justifyContent='flex-end'>
											<Button
												size='small'
												variant='contained'
												startIcon={<CheckIcon />}
												sx={{ background: mainGreen }}
												onClick={() => confirmPaymentRequest(item._id)}>
												Xác nhận
											</Button>
											<Button
												size='small'
												variant='outlined'
												color='error'
												startIcon={<CloseIcon />}
												onClick={() => rejectPaymentRequest(item._id)}>
												Từ chối
											</Button>
										</Stack>
									)}
									{mode === 'outgoing' && item.status === 'pending' && canManagePayment && (
										<Button
											size='small'
											variant='outlined'
											color='error'
											startIcon={<DeleteIcon />}
											onClick={() => deletePaymentRequest(item._id)}>
											Xóa
										</Button>
									)}
								</TableCell>
							</TableRow>
						)
					})}
				</TableBody>
			</Table>
		</Box>
	)
}

export function PaymentRequestDialog({
	canManagePayment,
	closePaymentRequestDialog,
	paymentRequestDialogOpen,
	paymentRequestForm,
	paymentSourceAccounts,
	savePaymentRequest,
	setPaymentRequestForm,
	transferredOrders,
}) {
	const selectedPaymentOrder = transferredOrders.find((order) => order._id === paymentRequestForm.orderId)
	const expectedAccountType = selectedPaymentOrder ? accountTypeForOrder(selectedPaymentOrder) : ''
	const needsSourceAccount = expectedAccountType === 'bank'
	const visiblePaymentSourceAccounts = expectedAccountType
		? paymentSourceAccounts.filter((account) => (account.accountType || 'bank') === expectedAccountType)
		: paymentSourceAccounts
	const selectedPaymentAccount = visiblePaymentSourceAccounts.find((account) => account._id === paymentRequestForm.bankAccount)

	return (
		<Dialog open={paymentRequestDialogOpen} onClose={closePaymentRequestDialog} fullWidth maxWidth='sm'>
			<DialogTitle>Gửi xác nhận thu tiền</DialogTitle>
			<DialogContent dividers>
				<Stack spacing={1.75} sx={{ pt: 0.5 }}>
					{selectedPaymentOrder && (
						<Box
							sx={{
								p: 1.5,
								border: '1px solid #DDE7DF',
								borderRadius: 2,
								background: '#F8FBF9',
							}}>
							<Stack spacing={1.25}>
								<Stack
									direction={{ xs: 'column', sm: 'row' }}
									spacing={1}
									alignItems={{ xs: 'flex-start', sm: 'center' }}
									justifyContent='space-between'>
										<Box sx={{ minWidth: 0 }}>
											<Typography sx={{ color: '#637381', fontSize: 12 }}>Đơn</Typography>
											<Typography sx={{ fontWeight: 900 }}>{orderCode(selectedPaymentOrder)}</Typography>
										</Box>
										<Chip size='small' label={paymentLabel(selectedPaymentOrder)} />
									</Stack>
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
										gap: 1.25,
									}}>
									<InfoItem label='Cửa hàng chuyển' value={selectedPaymentOrder.transferStore?.fromStore?.name} />
									<InfoItem label='Tổng đơn' value={formatMoney(orderTotal(selectedPaymentOrder))} strong />
									<InfoItem label='Khách' value={orderContactText(selectedPaymentOrder)} />
									<InfoItem label='Món' value={orderItemsText(selectedPaymentOrder)} />
								</Box>
								{needsSourceAccount && selectedPaymentAccount && (
									<Box sx={{ borderTop: '1px dashed #DDE7DF', pt: 1.25 }}>
										<Typography sx={{ color: '#637381', fontSize: 12 }}>Tài khoản cửa hàng chuyển</Typography>
										<Typography sx={{ fontWeight: 900 }}>{selectedPaymentAccount.name}</Typography>
										<Typography sx={{ color: '#637381', fontSize: 13, overflowWrap: 'anywhere' }}>
											{[
												accountTypeLabel(selectedPaymentAccount),
												selectedPaymentAccount.bankName,
												selectedPaymentAccount.accountNumber,
												selectedPaymentAccount.accountName,
											]
												.filter(Boolean)
												.join(' - ')}
										</Typography>
									</Box>
								)}
							</Stack>
						</Box>
					)}
					{needsSourceAccount ? (
						<FormControl fullWidth size='small'>
							<InputLabel>Tài khoản cửa hàng chuyển</InputLabel>
							<Select
								label='Tài khoản cửa hàng chuyển'
								value={paymentRequestForm.bankAccount}
								onChange={(event) => setPaymentRequestForm((prev) => ({ ...prev, bankAccount: event.target.value }))}>
								{visiblePaymentSourceAccounts.map((account) => (
									<MenuItem key={account._id} value={account._id}>
										{account.name} - {accountTypeLabel(account)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					) : selectedPaymentOrder ? (
						<Box sx={{ p: 1.25, border: '1px solid #DDE7DF', borderRadius: 2, background: '#F8FBF9' }}>
							<Chip
								size='small'
								color='success'
								label={`${accountTypeLabel(expectedAccountType)} mặc định`}
								sx={{ borderRadius: '8px', fontWeight: 800 }}
							/>
							<Typography sx={{ mt: 0.75, color: '#51665A', fontSize: 13, fontWeight: 700 }}>
								Cửa hàng chuyển không cần chọn tài khoản riêng cho đơn {paymentLabel(selectedPaymentOrder)}.
							</Typography>
						</Box>
					) : null}
					<TextField
						fullWidth
						size='small'
						label='Số tiền'
						value={paymentRequestForm.amount}
						onChange={(event) => setPaymentRequestForm((prev) => ({ ...prev, amount: event.target.value }))}
					/>
					<TextField
						fullWidth
						size='small'
						label='Ghi chú'
						multiline
						minRows={2}
						value={paymentRequestForm.content}
						onChange={(event) => setPaymentRequestForm((prev) => ({ ...prev, content: event.target.value }))}
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button startIcon={<CloseIcon />} onClick={closePaymentRequestDialog}>
					Hủy
				</Button>
				<Button
					variant='contained'
					startIcon={<SaveIcon />}
					disabled={!canManagePayment}
					sx={{ background: mainGreen }}
					onClick={savePaymentRequest}>
					Gửi xác nhận
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default function PaymentConfirmationsTab({
	canManagePayment,
	confirmPaymentRequest,
	deletePaymentRequest,
	rejectPaymentRequest,
	summary,
}) {
	const incoming = summary?.paymentConfirmations?.incoming || []
	const outgoing = summary?.paymentConfirmations?.outgoing || []

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
			<Paper elevation={0} sx={sectionSx}>
				<Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 2 }}>
					<Typography sx={{ fontWeight: 800 }}>Cần cửa hàng này xác nhận</Typography>
					<Chip size='small' color={incoming.some((item) => item.status === 'pending') ? 'warning' : 'success'} label={incoming.length} />
				</Stack>
				<ConfirmationTable
					canManagePayment={canManagePayment}
					confirmPaymentRequest={confirmPaymentRequest}
					deletePaymentRequest={deletePaymentRequest}
					items={incoming}
					mode='incoming'
					rejectPaymentRequest={rejectPaymentRequest}
				/>
			</Paper>

			<Paper elevation={0} sx={sectionSx}>
				<Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 2 }}>
					<Typography sx={{ fontWeight: 800 }}>Yêu cầu đã gửi</Typography>
					<Chip size='small' label={outgoing.length} />
				</Stack>
				<ConfirmationTable
					canManagePayment={canManagePayment}
					confirmPaymentRequest={confirmPaymentRequest}
					deletePaymentRequest={deletePaymentRequest}
					items={outgoing}
					mode='outgoing'
					rejectPaymentRequest={rejectPaymentRequest}
				/>
			</Paper>
		</Box>
	)
}
